/**
 * Main App component with routing
 * Uses lazy loading for admin module to reduce initial bundle size
 */

import { Routes, Route, Outlet } from 'react-router-dom';
import { lazy, Suspense, useState, useEffect } from 'react';

// Layouts (eagerly loaded for core UX)
import MainLayout from './components/layout/MainLayout';
import AuthLayout from './components/layout/AuthLayout';

// Core Pages (eagerly loaded for main user experience)
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import DashboardPage from './pages/DashboardPage';
import MatchesPage from './pages/MatchesPage';
import MatchDetailPage from './pages/MatchDetailPage';
import PoolsPage from './pages/PoolsPage';
import PoolCreatePage from './pages/PoolCreatePage';
import PoolDetailPage from './pages/PoolDetailPage';
import PoolRankingPage from './pages/PoolRankingPage';
import PoolPredictionsPage from './pages/PoolPredictionsPage';
import PoolSpecialsPage from './pages/PoolSpecialsPage';
import RulesPage from './pages/RulesPage';
import ProfilePage from './pages/ProfilePage';
import SecurityPage from './pages/SecurityPage';
import NotFoundPage from './pages/NotFoundPage';

// Admin Pages (lazy loaded - only admins need these)
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminUserDetail = lazy(() => import('./pages/admin/AdminUserDetail'));
const AdminPools = lazy(() => import('./pages/admin/AdminPools'));
const AdminPredictions = lazy(() => import('./pages/admin/AdminPredictions'));
const AdminAudit = lazy(() => import('./pages/admin/AdminAudit'));
const AdminSystem = lazy(() => import('./pages/admin/AdminSystem'));

// Components
import Toaster from './components/ui/toaster';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Hooks
import { useCurrentUser } from './hooks/useAuth';

// Error boundaries (eagerly loaded for error catching)
import AppErrorBoundary from './components/AppErrorBoundary';
import AdminErrorBoundary from './components/admin/AdminErrorBoundary';

// Admin loading fallback
function AdminLoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="mt-4 text-gray-300">Carregando painel administrativo...</p>
      </div>
    </div>
  );
}

// Suspense wrapper for admin routes with error boundary
function AdminSuspenseWrapper() {
  return (
    <AdminErrorBoundary>
      <Suspense fallback={<AdminLoadingFallback />}>
        <Outlet />
      </Suspense>
    </AdminErrorBoundary>
  );
}

// Timeout for auth loading to prevent infinite spinner when API hangs
const AUTH_TIMEOUT_MS = 10000;

export default function App() {
  // Fetch current user on app load
  const { isLoading, error, refetch } = useCurrentUser();
  const [timedOut, setTimedOut] = useState(false);

  // Reset timeout state when loading state changes
  useEffect(() => {
    if (!isLoading) {
      setTimedOut(false);
      return;
    }

    const timer = setTimeout(() => {
      if (isLoading) {
        setTimedOut(true);
      }
    }, AUTH_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [isLoading]);

  // Show error state with retry button if timed out or there's an error
  if (timedOut || error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 mx-auto mb-4 text-red-500">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {timedOut ? 'Conexão lenta' : 'Erro de conexão'}
          </h2>
          <p className="text-gray-600 mb-6">
            {timedOut
              ? 'O servidor está demorando para responder. Verifique sua conexão e tente novamente.'
              : 'Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.'}
          </p>
          <button
            onClick={() => {
              setTimedOut(false);
              refetch();
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <AppErrorBoundary>
      <Routes>
        {/* Public landing page */}
        <Route path="/" element={<LandingPage />} />

        {/* Auth routes (guest only) */}
        <Route element={<AuthLayout />}>
          <Route path="/entrar" element={<LoginPage />} />
          <Route path="/cadastro" element={<RegisterPage />} />
          <Route path="/recuperar-senha" element={<ForgotPasswordPage />} />
          <Route path="/resetar-senha/:token" element={<ResetPasswordPage />} />
          <Route path="/verificar-email" element={<VerifyEmailPage />} />
        </Route>

        {/* Protected routes (authenticated only) */}
        <Route element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
          <Route path="/inicio" element={<DashboardPage />} />
          <Route path="/jogos" element={<MatchesPage />} />
          <Route path="/jogos/:id" element={<MatchDetailPage />} />
          <Route path="/boloes" element={<PoolsPage />} />
          <Route path="/boloes/criar" element={<PoolCreatePage />} />
          <Route path="/boloes/:slug" element={<PoolDetailPage />} />
          <Route path="/boloes/:slug/ranking" element={<PoolRankingPage />} />
          <Route path="/boloes/:slug/palpites" element={<PoolPredictionsPage />} />
          <Route path="/boloes/:slug/especiais" element={<PoolSpecialsPage />} />
          <Route path="/regras" element={<RulesPage />} />
          <Route path="/perfil" element={<ProfilePage />} />
          <Route path="/seguranca" element={<SecurityPage />} />
        </Route>

        {/* Admin routes (lazy loaded) - require admin role */}
        <Route element={<AdminSuspenseWrapper />}>
          <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminLayout /></ProtectedRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="users/:id" element={<AdminUserDetail />} />
            <Route path="pools" element={<AdminPools />} />
            <Route path="matches" element={<AdminPage />} />
            <Route path="predictions" element={<AdminPredictions />} />
            <Route path="audit" element={<AdminAudit />} />
            <Route path="system" element={<AdminSystem />} />
          </Route>
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      {/* Global toast notifications (sonner) */}
      <Toaster />
    </AppErrorBoundary>
  );
}
